import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { supabase } from '../lib/supabase';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

interface LoginFormProps {
    setShowLogin: (show: boolean) => void;
    onSwitchToRegister?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ setShowLogin, onSwitchToRegister }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [emailNotConfirmed, setEmailNotConfirmed] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const handleResendVerification = async () => {
        if (!email) {
            toast.warning('Por favor, ingresa tu correo electrónico');
            return;
        }

        try {
            const { error } = await supabase.auth.resend({
                type: 'signup',
                email: email.trim()
            });

            if (error) throw error;
            toast.success('Se ha enviado un nuevo correo de verificación');
        } catch (error: any) {
            console.error('Error al reenviar verificación:', error);
            toast.error(error.message || 'Error al enviar el correo de verificación');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setEmailNotConfirmed(false);

        try {
            console.log('🔍 Starting login process for:', email.trim());
            
            // 1. Authenticate with Supabase
            const { data, error } = await supabase.auth.signInWithPassword({
                email: email.trim(),
                password
            });

            if (error) {
                console.error('❌ Supabase auth error:', error);
                if (error.message === 'Email not confirmed') {
                    setEmailNotConfirmed(true);
                    toast.warning('Por favor verifica tu correo electrónico');
                } else if (error.message.includes('Invalid login credentials') || error.message.includes('invalid_grant')) {
                    toast.error('Correo o contraseña incorrectos');
                } else {
                    toast.error(error.message || 'Error al iniciar sesión');
                }
                return;
            }

            if (!data?.user || !data?.session) {
                toast.error('No se pudo establecer la sesión');
                return;
            }

            console.log('✅ Supabase auth successful for user:', data.user.id);

            // 2. Update local auth state with user info
            const loginResult = await login(data.session.access_token, data.user);

            toast.success('¡Inicio de sesión exitoso!');
            setShowLogin(false);

            // 3. Redirección según rol
            const profile = loginResult?.profile;
            const isAdmin = profile?.client_type === 'admin' || profile?.role === 'admin' || data.user.user_metadata?.role === 'admin';

            if (location.state?.from) {
                navigate(location.state.from.pathname, { replace: true });
            } else if (isAdmin) {
                navigate('/admin', { replace: true });
            } else {
                navigate('/', { replace: true });
            }

        } catch (error: any) {
            console.error('Login error:', error);
            toast.error(error.message || 'Error inesperado al iniciar sesión');
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!email) {
            toast.warning('Por favor, ingresa tu correo electrónico');
            return;
        }

        try {
            const redirectUrl = `${window.location.origin}/reset-password`;
            const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
                redirectTo: redirectUrl,
            });

            if (error) throw error;

            toast.success('Se ha enviado un enlace de recuperación a tu correo');
        } catch (error: any) {
            console.error('Error al enviar email de recuperación:', error);
            toast.error(error.message || 'Error al enviar email de recuperación');
        }
    };

    return (
        <div className="w-full max-w-md">
            <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                    <label htmlFor="login-email" className="block text-xs font-semibold text-gray-700 mb-1">
                        Correo electrónico *
                    </label>
                    <input
                        id="login-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="tu@correo.com"
                        className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                        required
                    />
                </div>

                {emailNotConfirmed && (
                    <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-4">
                        <div className="flex">
                            <div className="ml-1">
                                <h3 className="text-xs font-bold text-yellow-800">
                                    Correo no verificado
                                </h3>
                                <div className="mt-1 text-xs text-yellow-700">
                                    <p>
                                        Por favor verifica tu correo electrónico para poder acceder.
                                        ¿No lo recibiste?{' '}
                                        <button
                                            type="button"
                                            onClick={handleResendVerification}
                                            className="font-bold underline hover:text-yellow-900"
                                        >
                                            Reenviar correo
                                        </button>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div>
                    <label htmlFor="login-password" className="block text-xs font-semibold text-gray-700 mb-1">
                        Contraseña *
                    </label>
                    <div className="relative">
                        <input
                            id="login-password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent pr-10 transition-all"
                            required
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                            {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                            ) : (
                                <Eye className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                </div>

                <div className="flex items-center justify-between text-xs">
                    <label className="flex items-center gap-2 cursor-pointer text-gray-600">
                        <input
                            type="checkbox"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            className="h-4 w-4 text-amber-500 focus:ring-amber-500 border-gray-300 rounded"
                        />
                        <span>Recordarme</span>
                    </label>

                    <button
                        type="button"
                        onClick={handleForgotPassword}
                        className="font-medium text-amber-600 hover:text-amber-500"
                    >
                        ¿Olvidaste tu contraseña?
                    </button>
                </div>

                <div>
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 active:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {isLoading ? (
                            <span className="flex items-center gap-2">
                                <Loader2 className="animate-spin h-4 w-4 text-white" />
                                <span>Iniciando sesión...</span>
                            </span>
                        ) : (
                            'Iniciar Sesión'
                        )}
                    </button>
                </div>
            </form>

            {onSwitchToRegister && (
                <div className="mt-6">
                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-200"></div>
                        </div>
                        <div className="relative flex justify-center text-xs">
                            <span className="px-2 bg-white text-gray-500">
                                ¿No tienes una cuenta?
                            </span>
                        </div>
                    </div>

                    <div className="mt-4">
                        <button
                            type="button"
                            onClick={onSwitchToRegister}
                            className="w-full flex justify-center py-2.5 px-4 border border-amber-500 text-amber-600 rounded-xl text-sm font-semibold hover:bg-amber-50 transition-colors"
                        >
                            Crear cuenta nueva
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoginForm;
